'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Lock, LogIn, UserPlus, ShieldCheck, Sparkles, CheckCircle2 } from 'lucide-react';

interface RestrictedAccessGateProps {
  title: string;
  subtitle: string;
  featureName?: string;
  redirectUrl?: string;
}

export function RestrictedAccessGate({
  title,
  subtitle,
  featureName,
  redirectUrl = '/login',
}: RestrictedAccessGateProps) {
  return (
    <div className="container mx-auto max-w-4xl px-4 py-16 sm:py-24">
      <Card className="relative overflow-hidden border-2 border-red-500/20 bg-gradient-to-b from-white via-slate-50 to-blue-50/30 shadow-2xl rounded-3xl dark:from-slate-900 dark:via-slate-900/90 dark:to-slate-950">
        {/* Decorative Top Accent Bar: Mechanical Red & ITN Blue */}
        <div className="h-3 w-full bg-gradient-to-r from-red-600 via-rose-500 to-blue-600" />

        {/* Ambient Glows */}
        <div className="absolute top-10 left-10 -z-10 h-64 w-64 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-10 -z-10 h-64 w-64 rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />

        <CardContent className="p-8 sm:p-12 text-center space-y-8">
          {/* Logo & Lock Badge */}
          <div className="relative mx-auto flex h-24 w-24 items-center justify-center">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-red-600/20 to-blue-600/20 blur-md animate-pulse" />
            <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-white p-2 shadow-lg border-2 border-red-500/30 dark:bg-slate-800">
              <Image
                src="/images/logo-ika-mesin.png"
                alt="Logo IKA MESIN ITN MALANG"
                width={70}
                height={70}
                className="object-contain"
                priority
              />
            </div>
            <div className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-md border-2 border-white dark:border-slate-800">
              <Lock className="h-4 w-4" />
            </div>
          </div>

          {/* Heading */}
          <div className="max-w-2xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-4 py-1.5 text-xs font-bold text-red-600 uppercase tracking-wider dark:text-red-400">
              <ShieldCheck className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span>Akses Khusus Anggota Terdaftar</span>
            </div>

            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 dark:text-white tracking-tight leading-snug">
              {title}
            </h2>

            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
              {subtitle}
            </p>
          </div>

          {/* Security & Benefits Badges */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl mx-auto text-left">
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/80 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700 shadow-sm text-xs font-medium text-slate-700 dark:text-slate-200">
              <CheckCircle2 className="h-4 w-4 text-blue-600 shrink-0" />
              <span>Perlindungan Privasi Data Alumni</span>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/80 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700 shadow-sm text-xs font-medium text-slate-700 dark:text-slate-200">
              <CheckCircle2 className="h-4 w-4 text-red-600 shrink-0" />
              <span>Khusus Teknik Mesin ITN Malang</span>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/80 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700 shadow-sm text-xs font-medium text-slate-700 dark:text-slate-200">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>Verifikasi Langsung via NIM & Email</span>
            </div>
          </div>

          {/* Action CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Button
              asChild
              size="lg"
              className="w-full sm:w-auto rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold shadow-lg shadow-red-600/25 px-8 h-12"
            >
              <Link href={`/login?redirect=${encodeURIComponent(redirectUrl)}`}>
                <LogIn className="mr-2 h-4 w-4" />
                <span>Masuk ke Akun Anda</span>
              </Link>
            </Button>

            <Button
              asChild
              variant="outline"
              size="lg"
              className="w-full sm:w-auto rounded-xl border-2 border-blue-600 text-blue-700 bg-blue-50/50 hover:bg-blue-600 hover:text-white dark:text-blue-400 dark:border-blue-500 font-bold px-8 h-12 transition-colors"
            >
              <Link href="/register">
                <UserPlus className="mr-2 h-4 w-4" />
                <span>Daftar / Verifikasi Alumni</span>
              </Link>
            </Button>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            Belum punya akun? Registrasi hanya membutuhkan waktu 2 menit dengan memasukkan NIM Teknik Mesin ITN Anda.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
